import type { CentralIconBaseProps } from 'central-icons/CentralIconBase';
import type React from 'react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';

import { FeatureFlag } from '@revelio/core';
import { IconButton, RevelioTooltip } from '@revelio/ui';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';

interface ActionBarIconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  icon: React.FC<CentralIconBaseProps>;
  iconSize?: number;
  'aria-label'?: string;
  tooltipLabel?: string;
  /** Accepted for compatibility with the previous Chakra-based API. */
  isDisabled?: boolean;
}

export const ActionBarIconButton = forwardRef<
  HTMLButtonElement,
  ActionBarIconButtonProps
>(function ActionBarIconButton(
  {
    icon: IconComponent,
    iconSize = 16,
    'aria-label': ariaLabel,
    tooltipLabel,
    isDisabled,
    disabled,
    ...props
  },
  ref
) {
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const button = (
    <IconButton
      ref={ref}
      variant={isNewDashboardLayout ? 'ghost' : 'outlined'}
      size="md"
      icon={<IconComponent size={iconSize} />}
      aria-label={(ariaLabel || tooltipLabel) ?? ''}
      disabled={disabled ?? isDisabled}
      {...props}
    />
  );

  if (tooltipLabel) {
    return <RevelioTooltip label={tooltipLabel}>{button}</RevelioTooltip>;
  }

  return button;
});

ActionBarIconButton.displayName = 'ActionBarIconButton';
